import { type ColumnDef } from '@tanstack/react-table';
import { QUERY, useSuspenseQueryOperation } from 'api/Query';
import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { type JSX, useMemo } from 'react';
import { SortableTable } from 'ts/base/table/SortableTable';
import { PathUtils } from 'ts/commons/PathUtils';
import { Modal, ScrollArea } from 'ts/components/Modal';
import { ToastNotification } from 'ts/components/Toast';
import type { ElementLocationSubtype } from 'typedefs/ElementLocationSubtype';
import type { ProjectBranchPath } from 'typedefs/ProjectBranchPath';
import type { TrackedFinding } from 'typedefs/TrackedFinding';

const NONE_SIBLING_ID = '';

type SiblingSelectionModalProps = {
	findingId: string | undefined;
	projectSettings: ProjectBranchPath;
	onClose: () => void;
	onSelection: (siblingId: string) => void;
};

/** Modal for choosing a task from the list of all open tasks. */
export function SiblingSelectionModal({
	onClose,
	findingId,
	projectSettings,
	onSelection
}: SiblingSelectionModalProps): JSX.Element | null {
	const siblingFindings: Array<Partial<TrackedFinding>> = useSuspenseQueryOperation(
		QUERY.getFindingSiblings(projectSettings.projectOrAlias!, findingId!, {
			t: new UnresolvedCommitDescriptor(null, projectSettings.branch)
		})
	);

	const columnDefinitions = useColumnDefinition(onSelection);

	if (siblingFindings.length === 0) {
		ToastNotification.info('No siblings available for the selected finding.');
		onClose();
		return null;
	}

	return (
		<Modal
			opened
			title="Select sibling"
			onClose={onClose}
			size="lg"
			scrollAreaComponent={ScrollArea.Autosize}
			data-testid="sibling-selection-modal"
		>
			<SortableTable
				striped
				unstackable
				selectable
				columnDefinitions={columnDefinitions}
				// This will be used to select no siblings
				data={[{ id: NONE_SIBLING_ID }, ...siblingFindings]}
			/>
		</Modal>
	);
}

function useColumnDefinition(onSelection: (siblingId: string) => void): Array<ColumnDef<Partial<TrackedFinding>>> {
	return useMemo(
		() => [
			{
				header: `Location`,
				accessorKey: 'location',
				cell: ({ row: { original } }) => {
					let content: JSX.Element | string = '<none>';
					if (original.id !== NONE_SIBLING_ID) {
						content = (
							<span data-testid="sibling-row">
								{getFindingLocationDescription(original as TrackedFinding)}
							</span>
						);
					}
					return (
						<div className="cursor-pointer" onClick={() => onSelection(original.id!)}>
							{content}
						</div>
					);
				}
			}
		],
		[onSelection]
	);
}

/**
 * Builds the text for a link referring to the finding location optionally relative to another finding location (i.e.,
 * sibling finding location).
 */
export function getFindingLocationDescription(
	finding: TrackedFinding,
	alternativeLocation?: ElementLocationSubtype,
	dataflow?: boolean
): string {
	const location = alternativeLocation ?? finding.location;
	let locationString = '';
	if (alternativeLocation?.uniformPath === finding.location.uniformPath && !dataflow) {
		locationString += 'same file';
	} else if ('displayPath' in location) {
		locationString += `${String(location.displayPath)}`;
	} else {
		locationString += location.uniformPath;
	}

	if (PathUtils.isTextRegionLocation(location) && !dataflow) {
		locationString += `:${location.rawStartLine}`;
		if (location.rawStartLine < location.rawEndLine) {
			locationString += `-${location.rawEndLine}`;
		}
	}

	if (PathUtils.isQualifiedNameLocation(location) && !('displayPath' in location)) {
		locationString += `:${location.qualifiedName}`;
	}

	return locationString;
}
